import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ConvexAuthProvider } from '@convex-dev/auth/react'
import { ConvexReactClient } from 'convex/react'
import * as Sentry from '@sentry/react'
import App from './App'
import './index.css'
import { captureAttribution } from '@/lib/attribution'
import { getOrCreateVisitorId } from '@/lib/analytics'
import { bindTrackClient } from '@/lib/track'
import { bindDownloadClient } from '@/lib/download'
import posthog from 'posthog-js'

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
  })
}

// Capture UTM params and referrer before React renders (OAuth redirect may strip them)
captureAttribution()
getOrCreateVisitorId()

if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: 'https://us.i.posthog.com',
    disable_session_recording: true,
    autocapture: false,
    capture_pageview: false,
    persistence: 'localStorage',
  })
}

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string, {
  unsavedChangesWarning: false,
})
bindTrackClient(convex)
bindDownloadClient(convex)

// Permanently suppress the native "Leave site? / changes may not be saved" dialog.
// We never want it — chat drafts and project state auto-save. Several libraries try to
// raise it (@convex-dev/auth during background token refresh, Convex core, analytics),
// so instead of racing their listeners by registration order we neutralize the only
// ways a beforeunload handler can cancel an unload: preventDefault(), assigning
// returnValue, and returning a value (the browser routes the return value through the
// returnValue setter). With all three inert, no handler — current or future — can
// summon the dialog, while non-cancelling handlers (e.g. analytics flush) still run.
if (typeof BeforeUnloadEvent !== 'undefined') {
  BeforeUnloadEvent.prototype.preventDefault = function () {}
  Object.defineProperty(BeforeUnloadEvent.prototype, 'returnValue', {
    configurable: true,
    get() { return '' },
    set() {},
  })
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ConvexAuthProvider client={convex}>
      <App />
    </ConvexAuthProvider>
  </StrictMode>,
)
